import {inject, Injectable} from '@angular/core';
import {CmsApiService} from '../../../core/cms-api.service';
import {SearchParameters} from '../../../core/definitions/search-parameters';
import {LoggerService} from '../../../core/logger.service';
import {SearchService} from '../../../core/search.service';

/** A fixed pick on a Named or Place sub-block: the picked record's id,
 * and which kind of sub-block picked it. */
export interface FixedPick {
  id: string;
  kind: 'named' | 'place';
}

/** The names `PickNamesService.names` found, by the picked record's id,
 * and the ids whose lookup answered, found or not. */
export interface PickNames {
  names: Map<string, string>;
  looked: Set<string>;
}

/** One lookup's answer: the names found, and the ids it answered for. */
interface PickNamesPart {
  names: [string, string][];
  looked: string[];
}

/**
 * The names of fixed picks, as their pickers show them: a Named pick's
 * record by its indexed name (`artifact_name`), all in one search, and a
 * Place by its own name (`/places/resolve`), one request each. A pick
 * whose lookup fails or finds no record gets no name; one whose lookup
 * fails is also left out of `looked`, and the failure is logged.
 */
@Injectable({providedIn: 'root'})
export class PickNamesService {
  private readonly searchService: SearchService = inject(SearchService);
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly logger: LoggerService = inject(LoggerService);

  /** Each of `picks`' name, by the picked record's id, and the ids whose
   * lookup answered: every Named id when the search did, and each Place
   * id whose resolve did. */
  async names(picks: readonly FixedPick[]): Promise<PickNames> {
    const ids = (kind: FixedPick['kind']) => [...new Set(picks.filter(p => p.kind === kind).map(p => p.id))];
    const parts = await Promise.all([this.namedNames(ids('named')), ...ids('place').map(id => this.placeName(id))]);
    return {
      names: new Map(parts.flatMap(part => part.names)),
      looked: new Set(parts.flatMap(part => part.looked)),
    };
  }

  private async namedNames(ids: string[]): Promise<PickNamesPart> {
    if (!ids.length) {
      return {names: [], looked: []};
    }
    const clause = ids.map(id => `"${id.replace(/["\\]/g, '')}"`).join(' OR ');
    try {
      const res = await this.searchService.search({
        fq: [`artifact_id:(${clause})`],
        fl: ['artifact_id', 'artifact_name'],
        rows: ids.length,
      } as SearchParameters);
      const names = (res.artifacts ?? [])
        .filter(art => art.artifact_id && art.artifact_name)
        .map((art): [string, string] => [art.artifact_id, art.artifact_name]);
      return {names, looked: ids};
    } catch (e) {
      this.logger.warn(`pick names: could not look up ${ids.join(', ')}: ${e}`);
      return {names: [], looked: []};
    }
  }

  private async placeName(id: string): Promise<PickNamesPart> {
    try {
      const res = await this.cms.importModulePlaceResolve({id});
      return {names: res.option?.name ? [[id, res.option.name]] : [], looked: [id]};
    } catch (e) {
      this.logger.warn(`pick names: could not resolve Place ${id}: ${e}`);
      return {names: [], looked: []};
    }
  }
}
